import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Spin, Tabs } from "antd";
import {
  ThunderboltOutlined,
  UnorderedListOutlined,
  MessageOutlined,
  BarChartOutlined,
  SettingOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import SetupTab from "./SetupTab";
import ConversationsTab from "./ConversationsTab";
import InsightsTab from "./InsightsTab";
import RulesTab from "./RulesTab";
import ConfigureChannelsDrawer from "./ConfigureChannelsDrawer";
import SonicLockPage from "./SonicLockPage";
import PlanEndedNotice from "components/General/PlanEndedNotice";
import { fetchRules } from "./service";
import { usePivonySonicSubscription } from "hooks/usePivonySonicSubscription";
import styles from "./style.module.scss";

const { TabPane } = Tabs;

const PRIMARY_TABS = ["conversations", "insights", "rules"];
const SETUP_PHASE_TABS = ["setup", "rules"];

const LEGACY_CONFIGURE_MAP = {
  voice: "voice",
  templates: "whatsapp",
  settings: "organization",
  integrations: "organization",
  team: "organization",
};

const LEGACY_TAB_ALIASES = {
  activity: "conversations",
};

/**
 * Pivony Sonic — operate-first: Conversations, Insights, Rules.
 * Channel setup (voice, WhatsApp, org settings) lives in Configure drawer.
 */
const Sonic = ({ authenticated, user, theme }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const requested = params.get("tab");
  // readOnly: the Engage plan has ended; rules, conversations and insights stay visible.
  const { canView, readOnly, loading: subscriptionLoading } = usePivonySonicSubscription(
    authenticated,
    user
  );

  const [rulesCount, setRulesCount] = useState(null);
  const [configureOpen, setConfigureOpen] = useState(false);
  const [configureSection, setConfigureSection] = useState("voice");
  const [configureFocus, setConfigureFocus] = useState(null);
  const legacyHandled = useRef(false);

  const refreshRulesCount = useCallback(async () => {
    if (!canView) {
      setRulesCount(0);
      return;
    }
    const res = await fetchRules();
    if (res && !res.error) {
      setRulesCount((res.rules || []).length);
    } else {
      setRulesCount(0);
    }
  }, [canView]);

  useEffect(() => {
    if (!subscriptionLoading) refreshRulesCount();
  }, [refreshRulesCount, subscriptionLoading]);

  const showSetupTab = !readOnly && rulesCount !== null && rulesCount === 0;
  const validTabs = showSetupTab ? SETUP_PHASE_TABS : PRIMARY_TABS;
  const defaultTab = showSetupTab ? "setup" : "conversations";

  const resolveTab = (tab) => {
    if (!tab) return defaultTab;
    if (LEGACY_TAB_ALIASES[tab]) return LEGACY_TAB_ALIASES[tab];
    if (LEGACY_CONFIGURE_MAP[tab]) return showSetupTab ? "setup" : "conversations";
    return validTabs.includes(tab) ? tab : defaultTab;
  };

  const activeTab = resolveTab(requested);

  useEffect(() => {
    if (legacyHandled.current || !requested) return;

    const configureSection = LEGACY_CONFIGURE_MAP[requested];
    const alias = LEGACY_TAB_ALIASES[requested];
    if (!configureSection && !alias) return;

    legacyHandled.current = true;
    if (configureSection && !readOnly) {
      setConfigureOpen(true);
      setConfigureSection(configureSection);
      const focus = params.get("focus");
      if (focus) setConfigureFocus(focus);
    }

    const nextTab = alias || (showSetupTab ? "setup" : "conversations");
    if (requested !== nextTab) {
      const next = new URLSearchParams(location.search);
      next.set("tab", nextTab);
      if (configureSection) {
        next.delete("focus");
      }
      history.replace({ pathname: location.pathname, search: next.toString() });
    }
  }, [requested, showSetupTab, readOnly, history, location.pathname, location.search, params]);

  const onTabChange = (key) => {
    const next = new URLSearchParams(location.search);
    next.set("tab", key);
    next.delete("create");
    next.delete("focus");
    history.replace({ pathname: location.pathname, search: next.toString() });
  };

  const navigateTab = (key, options = {}) => {
    if (LEGACY_CONFIGURE_MAP[key]) {
      setConfigureOpen(true);
      setConfigureSection(LEGACY_CONFIGURE_MAP[key]);
      if (options.focus) setConfigureFocus(options.focus);
      return;
    }
    const resolved = key === "activity" ? "conversations" : key;
    const next = new URLSearchParams(location.search);
    next.set("tab", resolved);
    next.delete("create");
    next.delete("focus");
    if (options.create) next.set("create", "1");
    if (options.focus) next.set("focus", options.focus);
    history.push({ pathname: location.pathname, search: next.toString() });
  };

  const openConfigure = (section = "voice", options = {}) => {
    setConfigureSection(section);
    if (options.focus) setConfigureFocus(options.focus);
    setConfigureOpen(true);
  };

  const goToInsights = () => onTabChange("insights");
  const goToRules = (options) => navigateTab("rules", options);

  const handleRulesChanged = () => {
    refreshRulesCount();
  };

  if (subscriptionLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.sonicLockSpinner}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (authenticated === "logged" && !canView) {
    return <SonicLockPage theme={theme} />;
  }

  const configureDrawer = (
    <ConfigureChannelsDrawer
      open={configureOpen}
      section={configureSection}
      focusTest={configureFocus === "test"}
      focusCompliance={params.get("focus") === "compliance"}
      onClose={() => {
        setConfigureOpen(false);
        setConfigureFocus(null);
      }}
      onSectionChange={setConfigureSection}
    />
  );

  if (showSetupTab) {
    return (
      <div className={styles.page}>
        <div className={styles.header}>
          <div className={styles.titleBlock}>
            <ThunderboltOutlined className={styles.titleIcon} />
            <div>
              <h1 className={styles.title}>{t("sonic.title")}</h1>
              <p className={styles.subtitle}>{t("sonic.subtitle")}</p>
            </div>
          </div>
        </div>

        {activeTab === "rules" && (
          <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            className={styles.setupBackLink}
            onClick={() => onTabChange("setup")}
          >
            {t("sonic.back_to_setup")}
          </Button>
        )}

        {activeTab === "setup" ? (
          <SetupTab active onNavigate={navigateTab} />
        ) : (
          <RulesTab
            active
            onRulesChanged={handleRulesChanged}
            setupMode
          />
        )}

        {configureDrawer}
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.titleBlock}>
          <ThunderboltOutlined className={styles.titleIcon} />
          <div>
            <h1 className={styles.title}>{t("sonic.title")}</h1>
            <p className={styles.subtitle}>{t("sonic.subtitle")}</p>
          </div>
        </div>
      </div>

      {readOnly ? <PlanEndedNotice product="engage" /> : null}

      <Tabs
        activeKey={activeTab}
        onChange={onTabChange}
        className={styles.tabs}
        tabBarExtraContent={
          readOnly ? null : (
            <Button
              type="default"
              icon={<SettingOutlined />}
              onClick={() => openConfigure("voice")}
            >
              {t("sonic.configure_channels")}
            </Button>
          )
        }
      >
        <TabPane
            tab={
              <span>
                <MessageOutlined /> {t("sonic.tab_conversations")}
              </span>
            }
            key="conversations"
          >
            <ConversationsTab
              active={activeTab === "conversations"}
              readOnly={readOnly}
              onOpenConfigure={openConfigure}
              onGoInsights={goToInsights}
            />
          </TabPane>
        <TabPane
            tab={
              <span>
                <BarChartOutlined /> {t("sonic.tab_insights")}
              </span>
            }
            key="insights"
          >
            <InsightsTab
              active={activeTab === "insights"}
              readOnly={readOnly}
              onManageRules={() => goToRules()}
              onCreateRule={() => goToRules({ create: true })}
              onTryVoiceAgent={() => openConfigure("voice", { focus: "test" })}
            />
          </TabPane>
        <TabPane
          tab={
            <span>
              <UnorderedListOutlined /> {t("sonic.tab_rules")}
            </span>
          }
          key="rules"
        >
          <RulesTab
            active={activeTab === "rules"}
            onRulesChanged={handleRulesChanged}
            readOnly={readOnly}
          />
        </TabPane>
      </Tabs>

      {configureDrawer}
    </div>
  );
};

export default Sonic;
